import React, { useCallback, useState } from "react";
import { Download, FileText, ImageOff, Play, VideoOff } from "lucide-react";
import type { Attachment } from "../../backend/api/chat";
import { attachmentThumbnailUrl, attachmentUrl } from "../../backend/api/chat";
import { cn } from "../../lib/utils";
import { MediaLightbox } from "./MediaLightbox";

type MessageAttachmentsProps = {
  attachments: Attachment[];
  /** Own messages hang off the right edge, like their bubble. */
  mine?: boolean;
};

/**
 * Attachments below a message bubble.
 *
 * Visual media and documents are laid out separately rather than sharing one
 * wrapping row: a square tile and a one line chip in the same row leaves a
 * ragged edge, and it changes with every message. Tiles sit in a row of equal
 * squares, documents stack underneath as full width chips, and the whole block
 * hugs the same edge as the bubble.
 *
 * Order is stable regardless of which upload finished first, so two files
 * picked together always appear the same way round.
 */
export function MessageAttachments({
  attachments,
  mine = false,
}: MessageAttachmentsProps) {
  if (!attachments.length) return null;

  const tiles = attachments.filter(
    (attachment) => attachment.kind === "image" || attachment.kind === "video",
  );
  const documents = attachments.filter(
    (attachment) => attachment.kind === "document",
  );

  return (
    <div
      className={cn(
        "mt-1 flex w-full max-w-[80%] flex-col gap-1.5",
        mine ? "items-end" : "items-start",
      )}
    >
      {tiles.length ? (
        <div
          className={cn(
            "flex flex-wrap gap-1.5",
            mine ? "justify-end" : "justify-start",
          )}
        >
          {tiles.map((attachment) => (
            <AttachmentTile key={attachment.id} attachment={attachment} />
          ))}
        </div>
      ) : null}

      {documents.map((attachment) => (
        <DocumentTile key={attachment.id} attachment={attachment} />
      ))}
    </div>
  );
}

function AttachmentTile({ attachment }: { attachment: Attachment }) {
  if (attachment.kind === "image") {
    return <ImageTile attachment={attachment} />;
  }
  if (attachment.kind === "video") {
    return <VideoTile attachment={attachment} />;
  }
  return <DocumentTile attachment={attachment} />;
}

/**
 * Opening the original attachment lives in one place so the click behaviour
 * is easy to swap for a lightbox later without touching the tile markup.
 */
function openAttachment(attachment: Attachment) {
  window.open(attachmentUrl(attachment.id), "_blank", "noopener,noreferrer");
}

type ThumbnailState = "ok" | "error" | "expired";

/**
 * A thumbnail can fail for two different reasons an `<img>` error event
 * cannot itself distinguish: a plain missing render (404, e.g. an
 * unsupported source like HEIC, or a video with no poster) versus bytes the
 * retention policy has removed (410). A HEAD request on the same URL after
 * the image fails tells the two apart.
 */
function useThumbnailState(attachment: Attachment): {
  state: ThumbnailState;
  onError: () => void;
} {
  // Always attempt the preview. `has_thumbnail` only says whether one is
  // already cached, and it is false for every attachment the sender has just
  // uploaded, because the API generates the preview on first request. Starting
  // from the flag would show a placeholder for exactly the image the sender is
  // looking at.
  const [state, setState] = useState<ThumbnailState>("ok");

  const onError = useCallback(() => {
    fetch(attachmentThumbnailUrl(attachment.id), {
      method: "HEAD",
      credentials: "include",
    })
      .then((response) => {
        setState(response.status === 410 ? "expired" : "error");
      })
      .catch(() => setState("error"));
  }, [attachment.id]);

  return { state, onError };
}

const TILE_BOX =
  "group/tile relative flex aspect-square w-32 items-center justify-center overflow-hidden rounded-lg bg-muted";

/**
 * Hover state for a tile. Clicking downloads the original, so the tile dims and
 * a download glyph appears rather than leaving the click unexplained.
 */
function DownloadOverlay({ label }: { label: string }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center justify-center bg-background/70 opacity-0 transition-opacity group-hover/tile:opacity-100 group-focus-visible/tile:opacity-100"
    >
      <span className="flex items-center gap-1.5 rounded-full bg-background/95 px-2.5 py-1 text-xs font-medium text-foreground shadow-sm">
        <Download className="h-3.5 w-3.5" />
        {label}
      </span>
    </span>
  );
}

function ImageTile({ attachment }: { attachment: Attachment }) {
  const { state, onError } = useThumbnailState(attachment);
  const name = attachment.original_name ?? "Attachment";

  return (
    <button
      type="button"
      onClick={() => openAttachment(attachment)}
      className={cn(TILE_BOX, "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
      aria-label={`Download image ${name}`}
    >
      {state === "expired" ? (
        <ExpiredNotice />
      ) : state === "error" ? (
        <ImageOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
      ) : (
        <>
          <img
            src={attachmentThumbnailUrl(attachment.id)}
            alt={name}
            loading="lazy"
            // The API is a different origin in development, and an <img> sends
            // no cookies cross-origin unless it asks to. Same-origin production
            // is unaffected.
            crossOrigin="use-credentials"
            onError={onError}
            className="h-full w-full object-cover transition-opacity"
          />
          <DownloadOverlay label="Download" />
        </>
      )}
    </button>
  );
}

/**
 * A video plays in the lightbox rather than downloading, which is what the play
 * glyph promises. The lightbox carries its own download button.
 */
function VideoTile({ attachment }: { attachment: Attachment }) {
  const { state, onError } = useThumbnailState(attachment);
  const [playing, setPlaying] = useState(false);
  const name = attachment.original_name ?? "Attachment";

  return (
    <>
      <button
        type="button"
        onClick={() => setPlaying(true)}
        className={cn(TILE_BOX, "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
        aria-label={`Play video ${name}`}
      >
        {state === "expired" ? (
          <ExpiredNotice />
        ) : (
          <>
            {state === "error" ? (
              <VideoOff className="h-6 w-6 text-muted-foreground" aria-hidden="true" />
            ) : (
              <img
                src={attachmentThumbnailUrl(attachment.id)}
                alt={name}
                loading="lazy"
                crossOrigin="use-credentials"
                onError={onError}
                className="h-full w-full object-cover"
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/20">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/65">
                <Play className="h-6 w-6 fill-white text-white" aria-hidden="true" />
              </span>
            </span>
          </>
        )}
      </button>
      <MediaLightbox
        attachment={playing ? attachment : null}
        onClose={() => setPlaying(false)}
      />
    </>
  );
}

function DocumentTile({ attachment }: { attachment: Attachment }) {
  const size = formatFileSize(attachment.size_bytes);
  return (
    <a
      href={attachmentUrl(attachment.id)}
      target="_blank"
      rel="noopener noreferrer"
      className="group/chip flex min-h-touch w-full max-w-xs items-center gap-2 md:min-h-0 rounded-lg border bg-background px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted hover:text-muted-foreground"
    >
      <span className="relative flex h-4 w-4 shrink-0 items-center justify-center">
        <FileText
          className="h-4 w-4 text-muted-foreground transition-opacity group-hover/chip:opacity-0"
          aria-hidden="true"
        />
        <Download
          className="absolute h-4 w-4 text-foreground opacity-0 transition-opacity group-hover/chip:opacity-100"
          aria-hidden="true"
        />
      </span>
      <span className="truncate">{attachment.original_name ?? "Attachment"}</span>
      {size ? (
        <span className="shrink-0 text-xs text-muted-foreground">{size}</span>
      ) : null}
    </a>
  );
}

function ExpiredNotice() {
  return <span className="text-xs text-muted-foreground">Expired</span>;
}

function formatFileSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(exponent ? 1 : 0)} ${units[exponent]}`;
}
